import React from 'react'
import { Navigate, useLocation, useParams } from 'react-router'
import { BrandAccessGuard } from '@/components/common'
import { useCampaignConfig } from '@/entities/campaignConfig'
import { useFetchBrandDetails } from '@/entities/metaLink/metaLink'
import { APP_ROUTES } from './routes.constants'
import { buildPlatformRoute, isBrandId, useBrandRoute } from './brandRoute'

// /shoppable-links/:brandId/* — the brand is in the path.
export const ShoppableBrandRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { brandId } = useParams<{ brandId: string }>()
  const { brand, isLoading, error, refetch } = useCampaignConfig()

  if (!isBrandId(brandId)) return <Navigate to={APP_ROUTES.SHOPPABLE_LINKS} replace />

  return (
    <BrandAccessGuard
      isLoading={isLoading}
      isReady={!!brand}
      error={error}
      homeRoute={APP_ROUTES.SHOPPABLE_LINKS}
      onRetry={() => refetch()}
    >
      {children}
    </BrandAccessGuard>
  )
}

// /meta/:brandId/* — the brand is in the path.
export const MetaBrandRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { brandId } = useBrandRoute()
  const { brandDetailsData, isLoading, error, fetchBrandDetails } = useFetchBrandDetails()

  if (!isBrandId(brandId)) {
    return <Navigate to={APP_ROUTES.META_COLLAB} replace />
  }

  return (
    <BrandAccessGuard
      isLoading={isLoading}
      isReady={!!brandDetailsData}
      error={error}
      homeRoute={APP_ROUTES.META_COLLAB}
      onRetry={() => fetchBrandDetails()}
    >
      {children}
    </BrandAccessGuard>
  )
}

// Old Meta links (?brandId=&platform=[&audienceId=]) move to the path form; other params are kept.
export const LegacyMetaBrandRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation()
  const params = new URLSearchParams(search)
  const brandId = params.get('brandId')
  const audienceId = params.get('audienceId') ?? undefined
  ;['brandId', 'platform', 'audienceId'].forEach((key) => params.delete(key))

  const needsAudience = to.includes(':audienceId')
  if (!isBrandId(brandId) || (needsAudience && !audienceId)) {
    return <Navigate to={APP_ROUTES.META_COLLAB} replace />
  }
  const rest = params.toString()
  const path = buildPlatformRoute(to, { brandId, audienceId })
  return <Navigate to={rest ? `${path}?${rest}` : path} replace />
}
